import React, { useEffect, useState } from 'react';
import { ModalAlert } from '@gpstrackit/gtc-react-controls';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import 'chart.js/auto';
import './index.scss';
import { searchAssetProfiles, searchAssetProfileEvents } from './redux/slice';
import Profiles from './components/Profiles';
import TableEvents from './components/TableEvents';
import HarshBrake from './components/HarshBrake';

const RiskEventsTab = () => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [noSaveChangesModalOpen, setNoSaveChangesModalOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const { profiles, loadingProfiles, riskEvents, loadingRiskEvents } = useSelector(state => state.driverPerformanceRiskEventsTabRedux || {});

    useEffect(() => {
        dispatch(searchAssetProfiles({ conditions: [], sort: [], limit: 100, nextToken: null }));
    }, [dispatch]);

    useEffect(() => {
        if (!loadingProfiles && profiles?.length > 0 && !selectedProfileId) {
            const defaultProfile = profiles.find(p => p.name === 'Default');
            setSelectedProfileId(defaultProfile?.id || profiles[0]?.id);
        }
    }, [profiles, loadingProfiles, selectedProfileId]);

    useEffect(() => {
        if (!selectedEvent?.eventId || loadingRiskEvents || !Array.isArray(riskEvents)) return;
        const updatedEvent = riskEvents.find(e => e?.eventId === selectedEvent.eventId && e?.profileId === selectedEvent.profileId);
        if (updatedEvent && (
            updatedEvent.status !== selectedEvent.status ||
            updatedEvent.coachable !== selectedEvent.coachable ||
            updatedEvent.severityParameters !== selectedEvent.severityParameters
        )) {
            setSelectedEvent(updatedEvent);
        }
    }, [riskEvents, loadingRiskEvents, selectedEvent]);

    useEffect(() => {
        if (!selectedProfileId || loadingRiskEvents || !Array.isArray(riskEvents) || riskEvents.length === 0) return;
        const first = riskEvents[0];
        const selectedBelongsToProfile = selectedEvent && String(selectedEvent.profileId) === String(selectedProfileId);
        const firstBelongsToProfile = first && String(first.profileId) === String(selectedProfileId);
        if (firstBelongsToProfile && !selectedBelongsToProfile) {
            setSelectedEvent(first);
        }
    }, [riskEvents, loadingRiskEvents, selectedEvent, selectedProfileId]);

    const handleSelectProfile = (profileId) => {
        if (hasUnsavedChanges && profileId !== selectedProfileId) {
            setPendingNavigation({ kind: 'profile', profileId });
            setNoSaveChangesModalOpen(true);
            return;
        }
        setSelectedProfileId(profileId);
        setSelectedEvent(null);
    };

    const handleSelectEvent = (event) => {
        if (hasUnsavedChanges && event?.id !== selectedEvent?.id) {
            setPendingNavigation({ kind: 'event', event });
            setNoSaveChangesModalOpen(true);
            return;
        }
        setSelectedEvent(event);
    };

    const handleModalAccept = () => {
        const pending = pendingNavigation;
        setNoSaveChangesModalOpen(false);
        setPendingNavigation(null);
        setHasUnsavedChanges(false);

        if (!pending) return;
        if (pending.kind === 'profile') {
            setSelectedProfileId(pending.profileId);
            setSelectedEvent(null);
            return;
        }
        if (pending.kind === 'event') {
            setSelectedEvent(pending.event);
        }
    };

    return (
        <section className="risk-events-tab-container">
            <Profiles selectedProfileId={selectedProfileId} onSelectProfile={handleSelectProfile} />
            <TableEvents selectedProfileId={selectedProfileId} selectedEvent={selectedEvent} onSelectEvent={handleSelectEvent} />
            <HarshBrake selectedEvent={selectedEvent} onUnsavedChangesChange={setHasUnsavedChanges} />
            <ModalAlert
                open={noSaveChangesModalOpen}
                title={messages['driverPerformanceConfig_riskEvents_modalNoSaveChanges_title']}
                text={messages['driverPerformanceConfig_riskEvents_modalNoSaveChanges_content']}
                acceptText={messages['driverPerformanceConfig_riskEvents_modalNoSaveChanges_accept']}
                cancelText={messages['driverPerformanceConfig_riskEvents_modalNoSaveChanges_cancel']}
                onClose={() => { setNoSaveChangesModalOpen(false); setPendingNavigation(null); }}
                onCancel={() => { setNoSaveChangesModalOpen(false); setPendingNavigation(null); }}
                onAccept={handleModalAccept}
            />
        </section>
    );
};

export default RiskEventsTab;
